<template>
  <article class="vecura-page">
    <section class="section vecura-hero" aria-labelledby="vecura-title">
      <div class="vecura-hero__content">
        <img
          class="vecura-hero__logo"
          src="@/assets/vecura-logo.svg"
          alt="Vecura"
        />
        <p>{{ vecuraPage.eyebrow }}</p>
        <h1 id="vecura-title">{{ vecuraPage.headline }}</h1>
        <span>{{ vecuraPage.intro }}</span>

        <p class="vecura-hero__contact">
          Kontakt über
          <a :href="vecuraWebsiteUrl" target="_blank" rel="noopener noreferrer">
            vecura.io </a
          >,
          <a
            :href="vecuraLinkedinUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            LinkedIn
          </a>
          oder <a :href="vecuraEmailHref">hello@vecura.io</a>.
        </p>
      </div>

      <aside class="vecura-hero__role">
        <strong>Meine Rolle</strong>
        <p>{{ vecuraPage.role }}</p>
        <ul>
          <li v-for="point in vecuraPage.rolePoints" :key="point">
            {{ point }}
          </li>
        </ul>
      </aside>
    </section>

    <section class="section vecura-pitch">
      <div class="vecura-pitch__copy">
        <p>Plattform</p>
        <h2>{{ vecuraPage.pitch }}</h2>
      </div>

      <div class="vecura-pitch__proof">
        <article v-for="point in vecuraPage.proofPoints" :key="point.value">
          <strong>{{ point.value }}</strong>
          <span>{{ point.label }}</span>
        </article>
      </div>
    </section>

    <section class="section vecura-capabilities">
      <SectionHeading
        eyebrow="Was Vecura leistet"
        title="Wert entsteht nicht im Modell allein, sondern im Zusammenspiel aus Daten, Suche und Kontext."
        description="Vecura verbindet Plattformbausteine mit individueller Konfiguration, damit Organisationen schneller zu belastbaren Wissensanwendungen kommen."
      />

      <div class="vecura-capabilities__grid">
        <article
          v-for="capability in vecuraPage.capabilities"
          :key="capability.title"
        >
          <h3>{{ capability.title }}</h3>
          <p>{{ capability.description }}</p>
        </article>
      </div>
    </section>

    <section class="section vecura-case">
      <div class="vecura-case__label">Case Study</div>
      <div class="vecura-case__body">
        <h2>{{ vecuraPage.caseStudy.title }}</h2>
        <p>{{ vecuraPage.caseStudy.summary }}</p>
        <ul>
          <li v-for="detail in vecuraPage.caseStudy.details" :key="detail">
            {{ detail }}
          </li>
        </ul>
      </div>
    </section>

    <section class="section vecura-contact">
      <div>
        <p>Kontakt</p>
        <h2>Wenn Fachwissen schneller nutzbar werden soll.</h2>
      </div>
      <div class="vecura-contact__actions">
        <a
          class="button-link button-link--yellow"
          :href="vecuraWebsiteUrl"
          target="_blank"
          rel="noopener noreferrer"
        >
          <ContactChannelIcon
            class="vecura-contact__brand-icon"
            type="vecura"
          />
          Website öffnen
          <ExternalLinkIcon class="vecura-contact__action-icon" />
        </a>
        <a
          class="button-link button-link--secondary"
          :href="vecuraLinkedinUrl"
          target="_blank"
          rel="noopener noreferrer"
        >
          <ContactChannelIcon
            class="vecura-contact__brand-icon"
            type="linkedin"
          />
          LinkedIn
          <ExternalLinkIcon class="vecura-contact__action-icon" />
        </a>
        <a class="button-link button-link--secondary" :href="vecuraEmailHref">
          <ContactChannelIcon class="vecura-contact__brand-icon" type="mail" />
          hello@vecura.io
          <ExternalLinkIcon class="vecura-contact__action-icon" />
        </a>
        <a class="vecura-contact__back" href="/">
          <ArrowLeftIcon class="vecura-contact__back-icon" />
          Zurück zum Profil
        </a>
      </div>
    </section>
  </article>
</template>

<script setup lang="ts">
import ArrowLeftIcon from "@/components/icons/ArrowLeftIcon.vue";
import ContactChannelIcon from "@/components/icons/ContactChannelIcon.vue";
import ExternalLinkIcon from "@/components/icons/ExternalLinkIcon.vue";
import SectionHeading from "@/components/SectionHeading.vue";
import { vecuraPage } from "@/data/profile";

const vecuraWebsiteUrl = "https://vecura.io";
const vecuraEmailHref = "mailto:hello@vecura.io";
const vecuraLinkedinUrl = "https://www.linkedin.com/company/vecura-gmbh";
</script>

<style scoped>
.vecura-page {
  --vecura-bg: #0e151c;
  --vecura-bg-soft: #111b24;
  --vecura-panel: rgba(229, 230, 231, 0.055);
  --vecura-panel-strong: rgba(229, 230, 231, 0.085);
  --vecura-line: rgba(229, 230, 231, 0.1);
  --vecura-text: #f4f7fa;
  --vecura-muted: rgba(229, 230, 231, 0.7);
  --vecura-muted-soft: rgba(229, 230, 231, 0.54);

  overflow-x: hidden;
  background: var(--vecura-bg);
  color: var(--vecura-text);
}

.vecura-hero {
  position: relative;
  isolation: isolate;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(300px, 0.42fr);
  gap: 64px;
  align-items: end;
  min-height: calc(100vh - 76px);
  background: linear-gradient(135deg, rgba(0, 181, 237, 0.2), transparent 34%),
    linear-gradient(180deg, #101a23 0%, var(--vecura-bg) 74%);
  padding-top: 86px;
  padding-bottom: 76px;
}

.vecura-hero::before {
  position: absolute;
  inset: 0;
  z-index: -1;
  background-image: linear-gradient(
      rgba(229, 230, 231, 0.05) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(229, 230, 231, 0.05) 1px, transparent 1px);
  background-size: 42px 42px;
  content: "";
  opacity: 0.2;
}

.vecura-hero__content,
.vecura-hero__role {
  position: relative;
}

.vecura-hero__logo {
  display: block;
  width: min(330px, 74vw);
  height: auto;
  margin-bottom: 42px;
}

.vecura-hero__content > p:not(.vecura-hero__contact),
.vecura-pitch__copy p,
.vecura-contact p {
  margin: 0 0 14px;
  color: var(--color-vecura);
  font-size: 0.82rem;
  font-weight: 900;
  text-transform: uppercase;
}

h1 {
  max-width: 930px;
  margin: 0;
  color: var(--vecura-text);
  font-size: 4rem;
  line-height: 1;
}

.vecura-hero__content > span {
  display: block;
  max-width: 780px;
  margin-top: 26px;
  color: var(--vecura-muted);
  font-size: 1.12rem;
  line-height: 1.72;
}

.vecura-hero__contact {
  margin: 26px 0 0;
  color: var(--vecura-muted-soft);
  font-size: 1rem;
  line-height: 1.6;
  text-transform: none;
}

.vecura-hero__contact a {
  color: var(--vecura-text);
  font-weight: 850;
  text-decoration-color: rgba(0, 181, 237, 0.72);
  text-underline-offset: 4px;
  transition: color 180ms ease, text-decoration-color 180ms ease;
}

.vecura-hero__contact a:hover {
  color: var(--color-vecura);
  text-decoration-color: currentColor;
}

.vecura-hero__role {
  border: 1px solid var(--vecura-line);
  border-radius: 8px;
  background: var(--vecura-panel);
  padding: 26px;
}

.vecura-hero__role strong {
  display: block;
  color: var(--vecura-text);
  font-size: 1.05rem;
}

.vecura-hero__role p {
  margin: 14px 0 0;
  color: var(--vecura-muted);
  line-height: 1.7;
}

.vecura-hero__role ul {
  display: grid;
  gap: 10px;
  margin: 22px 0 0;
  padding: 0;
  list-style: none;
}

.vecura-hero__role li {
  position: relative;
  padding-left: 18px;
  color: rgba(229, 230, 231, 0.8);
  font-size: 0.94rem;
  line-height: 1.55;
}

.vecura-hero__role li::before {
  position: absolute;
  top: 0.72em;
  left: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-vecura);
  content: "";
}

.vecura-page .button-link--yellow {
  border-color: var(--color-vecura);
  background: var(--color-vecura);
  color: #ffffff;
}

.vecura-page .button-link--yellow:hover {
  border-color: #39cdf7;
  background: #18c3f3;
  box-shadow: 0 14px 32px rgba(0, 181, 237, 0.18);
  color: #ffffff;
}

.vecura-page .button-link--secondary {
  border-color: rgba(229, 230, 231, 0.38);
  background: transparent;
  color: var(--vecura-text);
}

.vecura-page .button-link--secondary:hover {
  border-color: rgba(229, 230, 231, 0.64);
  background: rgba(229, 230, 231, 0.08);
  box-shadow: 0 14px 32px rgba(0, 0, 0, 0.16);
}

.vecura-pitch {
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(360px, 1fr);
  gap: 56px;
  align-items: start;
  background: var(--vecura-bg-soft);
}

.vecura-pitch__copy h2 {
  margin: 0;
  color: var(--vecura-text);
  font-size: 2.1rem;
  line-height: 1.2;
}

.vecura-pitch__proof {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.vecura-pitch__proof article {
  border: 1px solid var(--vecura-line);
  border-radius: 8px;
  background: var(--vecura-panel);
  padding: 20px;
}

.vecura-pitch__proof strong,
.vecura-pitch__proof span {
  display: block;
}

.vecura-pitch__proof strong {
  color: var(--color-vecura);
  font-size: 1.75rem;
  line-height: 1;
}

.vecura-pitch__proof span {
  margin-top: 12px;
  color: var(--vecura-muted);
  line-height: 1.55;
}

.vecura-capabilities {
  background: var(--vecura-bg);
}

.vecura-capabilities :deep(.section-heading__eyebrow) {
  color: var(--color-vecura);
}

.vecura-capabilities :deep(h2) {
  color: var(--vecura-text);
}

.vecura-capabilities :deep(.section-heading__description) {
  color: var(--vecura-muted);
}

.vecura-capabilities__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-top: 38px;
}

.vecura-capabilities article {
  border: 1px solid var(--vecura-line);
  border-radius: 8px;
  background: var(--vecura-panel);
  padding: 24px;
}

.vecura-capabilities h3 {
  margin: 0;
  color: var(--vecura-text);
  font-size: 1.18rem;
}

.vecura-capabilities p {
  margin: 14px 0 0;
  color: var(--vecura-muted);
  line-height: 1.65;
}

.vecura-case {
  display: grid;
  grid-template-columns: 160px minmax(0, 1fr);
  gap: 42px;
  background: var(--vecura-bg-soft);
}

.vecura-case__label {
  color: var(--color-vecura);
  font-size: 0.82rem;
  font-weight: 900;
  text-transform: uppercase;
}

.vecura-case h2 {
  margin: 0;
  color: var(--vecura-text);
  font-size: 2rem;
  line-height: 1.18;
}

.vecura-case p {
  max-width: 820px;
  margin: 18px 0 0;
  color: var(--vecura-muted);
  font-size: 1.05rem;
  line-height: 1.7;
}

.vecura-case ul {
  display: grid;
  gap: 12px;
  max-width: 820px;
  margin: 24px 0 0;
  padding: 0;
  list-style: none;
}

.vecura-case li {
  position: relative;
  padding-left: 20px;
  color: rgba(229, 230, 231, 0.78);
  line-height: 1.65;
}

.vecura-case li::before {
  position: absolute;
  top: 0.7em;
  left: 0;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--color-vecura);
  content: "";
}

.vecura-contact {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 32px;
  align-items: center;
  background: var(--vecura-bg);
}

.vecura-contact h2 {
  max-width: 700px;
  margin: 0;
  color: var(--vecura-text);
  font-size: 2rem;
  line-height: 1.18;
}

.vecura-contact__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: flex-end;
}

.vecura-contact__brand-icon,
.vecura-contact__action-icon {
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
  transition: transform 180ms ease;
}

.vecura-contact__actions .button-link:hover .vecura-contact__action-icon {
  transform: translate(2px, -2px);
}

.vecura-contact__brand-icon {
  opacity: 0.78;
}

.vecura-contact__brand-icon :deep(svg) {
  display: block;
  width: 100%;
  height: 100%;
}

.vecura-contact__back {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  color: var(--vecura-muted-soft);
  font-weight: 900;
  text-decoration: none;
  transition: color 180ms ease, transform 180ms ease;
}

.vecura-contact__back:hover {
  color: var(--vecura-text);
  transform: translateX(-2px);
}

.vecura-contact__back-icon {
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
}

@media (max-width: 980px) {
  .vecura-hero,
  .vecura-pitch,
  .vecura-case,
  .vecura-contact {
    grid-template-columns: 1fr;
  }

  h1 {
    font-size: 3rem;
  }

  .vecura-contact__actions {
    justify-content: flex-start;
  }
}

@media (max-width: 720px) {
  .vecura-hero {
    min-height: auto;
    padding-top: 64px;
    padding-bottom: 64px;
  }

  h1 {
    font-size: 2.2rem;
    line-height: 1.08;
  }

  .vecura-pitch__proof,
  .vecura-capabilities__grid {
    grid-template-columns: 1fr;
  }

  .vecura-contact__actions,
  .vecura-contact__actions a {
    width: 100%;
  }
}

@media (max-width: 760px) {
  .vecura-hero {
    gap: 28px;
    min-height: auto;
    padding-top: 44px;
    padding-bottom: 50px;
  }

  .vecura-hero__logo {
    width: min(220px, 72vw);
    margin-bottom: 34px;
  }

  .vecura-hero__content > p:not(.vecura-hero__contact),
  .vecura-pitch__copy p,
  .vecura-contact p {
    font-size: 0.72rem;
  }

  h1 {
    font-size: clamp(2.3rem, 11vw, 3rem);
    line-height: 1.03;
  }

  .vecura-hero__content > span {
    margin-top: 20px;
    font-size: 1rem;
    line-height: 1.62;
  }

  .vecura-hero__contact {
    margin-top: 20px;
    font-size: 0.94rem;
  }

  .vecura-hero__role {
    border-radius: 16px;
    padding: 20px;
  }

  .vecura-hero__role p,
  .vecura-hero__role li {
    font-size: 0.94rem;
    line-height: 1.58;
  }

  .vecura-pitch,
  .vecura-capabilities,
  .vecura-case,
  .vecura-contact {
    padding-top: 52px;
    padding-bottom: 52px;
  }

  .vecura-pitch {
    gap: 28px;
  }

  .vecura-pitch__copy h2,
  .vecura-case h2,
  .vecura-contact h2 {
    font-size: 1.58rem;
    line-height: 1.16;
  }

  .vecura-pitch__proof {
    gap: 10px;
  }

  .vecura-pitch__proof article,
  .vecura-capabilities article {
    border-radius: 16px;
    padding: 18px;
  }

  .vecura-pitch__proof strong {
    font-size: 1.5rem;
  }

  .vecura-capabilities__grid {
    gap: 12px;
    margin-top: 28px;
  }

  .vecura-capabilities h3 {
    font-size: 1.08rem;
  }

  .vecura-capabilities p,
  .vecura-case p,
  .vecura-case li {
    font-size: 0.96rem;
    line-height: 1.58;
  }

  .vecura-case {
    gap: 16px;
  }

  .vecura-case__label {
    font-size: 0.72rem;
  }

  .vecura-contact {
    align-items: start;
    gap: 24px;
  }

  .vecura-contact__actions {
    display: grid;
    width: 100%;
    gap: 10px;
  }

  .vecura-contact__actions .button-link {
    width: 100%;
    justify-content: space-between;
    padding-right: 16px;
    padding-left: 16px;
  }

  .vecura-contact__back {
    justify-content: center;
    min-height: 44px;
  }
}
</style>
